'use client';

import { ActionIcon, Box, Card, Image } from '@mantine/core';
import ChevronLeftIcon from '@material-design-icons/svg/outlined/chevron_left.svg';
import ChevronRightIcon from '@material-design-icons/svg/outlined/chevron_right.svg';
import { useState } from 'react';

import { TrackedButton } from '@/components';

import styles from './IssueCardCarousel.module.css';

interface IssueCardCarouselProps {
  images: string[];
  interval?: number;
  width?: number;
  height?: number;
  showBorder?: boolean;
  showIndicators?: boolean;
  showArrows?: boolean;
}

export function IssueCardCarousel({
  images,
  width = 400,
  height = 500,
  showBorder = false,
  showIndicators = false,
  showArrows = true,
}: IssueCardCarouselProps) {
  const [currentIndex, setCurrentIndex] = useState(0);

  const handleIndicatorClick = (index: number) => {
    setCurrentIndex(index);
  };

  const goToPrevious = () => {
    setCurrentIndex((prevIndex) =>
      prevIndex === 0 ? images.length - 1 : prevIndex - 1,
    );
  };

  const goToNext = () => {
    setCurrentIndex((prevIndex) => (prevIndex + 1) % images.length);
  };

  return (
    <Card
      variant={showBorder ? '' : 'borderless'}
      className={styles.carouselCard}
      shadow={showBorder ? 'sm' : 'none'}
      withBorder={showBorder}
      p={0}
    >
      <Box className={styles.imageContainer}>
        {images.map((src, index) => (
          <div
            key={index}
            className={`${styles.imageSlide} ${
              index === currentIndex ? styles.activeSlide : ''
            }`}
          >
            {index === currentIndex && (
              <Image
                src={src}
                alt={`Issue card ${index + 1}`}
                width={width}
                height={height}
                className={styles.image}
              />
            )}
          </div>
        ))}

        {showArrows && images.length > 1 && (
          <>
            <ActionIcon
              className={`${styles.arrowButton} ${styles.prevButton}`}
              variant="filled"
              radius="xl"
              onClick={goToPrevious}
              aria-label="Previous image"
            >
              <ChevronLeftIcon
                width={23}
                height={23}
              />
            </ActionIcon>

            <ActionIcon
              className={`${styles.arrowButton} ${styles.nextButton}`}
              variant="filled"
              radius="xl"
              onClick={goToNext}
              aria-label="Next image"
            >
              <ChevronRightIcon
                width={23}
                height={23}
              />
            </ActionIcon>
          </>
        )}
      </Box>

      {showIndicators && (
        <Box className={styles.indicatorContainer}>
          {images.map((_, index) => (
            <TrackedButton
              key={index}
              className={`${styles.indicator} ${
                index === currentIndex ? styles.activeIndicator : ''
              }`}
              onClick={() => handleIndicatorClick(index)}
              aria-label={`Go to slide ${index + 1}`}
              trackingName="issue_card_carousel_indicator_click"
              trackingProperties={{
                index: index,
              }}
            />
          ))}
        </Box>
      )}
    </Card>
  );
}

export default IssueCardCarousel;
